import React, { useEffect, useState } from "react";
import axios from "axios";
import { ListTodo, ChevronRight, AlertTriangle, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const endOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
};

/**
 * «Задачи на сегодня» — плашка сверху во всей админке. Показывает свои
 * активные задачи со сроком до конца сегодняшнего дня (включая просроченные),
 * чтобы менеджер на смене сразу отрабатывал их, а не искал по вкладкам.
 */
export default function TodayTasksBar() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/crm/tasks`, {
        params: { status: "pending", only_mine: true, limit: 100 },
        withCredentials: true,
      });
      const limit = endOfToday();
      const items = (r.data?.items || [])
        .filter(t => t.due_at && new Date(t.due_at) <= limit)
        .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
      setTasks(items);
    } catch (e) {
      setTasks([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 90_000);
    return () => clearInterval(iv);
  }, []);

  const goToTasks = () => {
    window.dispatchEvent(new CustomEvent("admin:navigate",
      { detail: { section: "crm", crm_tab: "today" } }));
  };

  if (loading || tasks.length === 0) return null;

  const now = new Date();
  const overdue = tasks.filter(t => new Date(t.due_at) < now);

  return (
    <div data-testid="today-tasks-bar"
      style={{
        background: "linear-gradient(135deg,#0A0A0A,#1c1c1c)",
        border: `1px solid ${overdue.length ? "#DC2626" : "#C5A059"}`,
        borderRadius: 10, padding: "12px 16px", marginBottom: 16,
        fontFamily: "Manrope", color: "#e5e7eb",
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <ListTodo size={16} color="#D4B878" />
        <b style={{ fontSize: 13, color: "#D4B878", letterSpacing: 0.3 }}>
          Задачи на сегодня — {tasks.length}
        </b>
        {overdue.length > 0 && (
          <span data-testid="today-tasks-overdue"
            style={{ background: "rgba(220,38,38,0.18)", border: "1px solid #DC2626",
                      color: "#FCA5A5", borderRadius: 20, padding: "2px 10px",
                      fontSize: 11, fontWeight: 700, display: "inline-flex",
                      alignItems: "center", gap: 5 }}>
            <AlertTriangle size={11} /> просрочено {overdue.length}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button data-testid="today-tasks-refresh" onClick={load}
          title="Обновить"
          style={{ background: "transparent", border: "1px solid #3f3f46",
                    color: "#a1a1aa", borderRadius: 6, padding: "5px 8px",
                    cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
          <RefreshCw size={12} />
        </button>
        <button data-testid="today-tasks-toggle" onClick={() => setExpanded(v => !v)}
          style={{ background: "transparent", border: "1px solid #3f3f46",
                    color: "#a1a1aa", borderRadius: 6, padding: "5px 10px",
                    cursor: "pointer", fontSize: 11.5 }}>
          {expanded ? "Свернуть" : "Показать"}
        </button>
        <button data-testid="today-tasks-open-crm" onClick={goToTasks}
          style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
                    border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer",
                    fontSize: 12, fontWeight: 800, display: "inline-flex",
                    alignItems: "center", gap: 5 }}>
          Отработать <ChevronRight size={13} />
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {tasks.slice(0, 6).map(t => {
            const late = new Date(t.due_at) < now;
            return (
              <div key={t.id} data-testid={`today-task-${t.id}`}
                onClick={goToTasks}
                style={{ display: "flex", alignItems: "center", gap: 10,
                          background: "rgba(255,255,255,0.04)",
                          border: `1px solid ${late ? "rgba(220,38,38,0.4)" : "rgba(197,160,89,0.25)"}`,
                          borderRadius: 8, padding: "8px 12px", cursor: "pointer",
                          fontSize: 12.5 }}>
                <span style={{ color: late ? "#FCA5A5" : "#D4B878", fontWeight: 700,
                                minWidth: 44 }}>
                  {fmtTime(t.due_at)}
                </span>
                <span style={{ color: "#e5e7eb", fontWeight: 600 }}>
                  {t.client_name || "Клиент"}
                </span>
                <span style={{ color: "#a1a1aa" }}>· {t.title}</span>
                <div style={{ flex: 1 }} />
                <ChevronRight size={14} color="#71717a" />
              </div>
            );
          })}
          {tasks.length > 6 && (
            <div style={{ fontSize: 11.5, color: "#a1a1aa" }}>
              и ещё {tasks.length - 6} — нажмите «Отработать»
            </div>
          )}
        </div>
      )}
    </div>
  );
}
